import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { History, RefreshCw, ChevronDown, ChevronUp, ScanLine, CheckCircle2, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtWhen = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const betLabel = (b) => {
    const digits = Array.isArray(b.digits) ? b.digits.join('') : '';
    const bt = (b.bet_type || '').slice(0, 3);
    return `${digits}${bt ? ' ' + bt : ''}`;
};

export const LotteryScanHistory = () => {
    const [open, setOpen] = useState(false);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [expanded, setExpanded] = useState(null);      // scan id
    const [images, setImages] = useState({});            // { id: dataUrl }

    const fetchHistory = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/lottery/scan-history?limit=40`, { withCredentials: true });
            setRows(data.rows || []);
            setLoaded(true);
        } catch {
            toast.error('Could not load scan history');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (open && !loaded) fetchHistory(); }, [open, loaded, fetchHistory]);

    const toggleRow = async (id) => {
        if (expanded === id) { setExpanded(null); return; }
        setExpanded(id);
        if (!images[id]) {
            try {
                const { data } = await axios.get(`${API}/admin/lottery/scans/${id}`, { withCredentials: true });
                if (data.image_base64) {
                    const mime = data.mime_type || 'image/jpeg';
                    setImages(prev => ({ ...prev, [id]: `data:${mime};base64,${data.image_base64}` }));
                }
            } catch { /* image may have expired (30d TTL) */ }
        }
    };

    return (
        <div className="rounded-xl border border-white/10 bg-[#0f0f0f] overflow-hidden" data-testid="scan-history-panel">
            <button onClick={() => setOpen(o => !o)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition"
                data-testid="scan-history-toggle">
                <div className="flex items-center gap-2">
                    <History className="w-4 h-4 text-[#00E5FF]" />
                    <span className="text-sm font-black text-white">Scan History</span>
                    <span className="text-[11px] text-[#A0AAB2]">· photo audit of every ticket scan</span>
                </div>
                {open ? <ChevronUp className="w-4 h-4 text-[#A0AAB2]" /> : <ChevronDown className="w-4 h-4 text-[#A0AAB2]" />}
            </button>

            {open && (
                <div className="px-4 pb-4">
                    <div className="flex justify-end mb-2">
                        <Button size="sm" variant="outline" onClick={fetchHistory} disabled={loading}
                            className="border-white/15 text-[#A0AAB2] hover:text-white h-8" data-testid="scan-history-refresh">
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        </Button>
                    </div>

                    {loading && rows.length === 0 ? (
                        <p className="text-center text-[#A0AAB2] text-sm py-6">Loading…</p>
                    ) : rows.length === 0 ? (
                        <p className="text-center text-[#A0AAB2] text-sm py-6">No scans yet. Scanned tickets show up here for 30 days.</p>
                    ) : (
                        <div className="space-y-1.5 max-h-96 overflow-y-auto" data-testid="scan-history-list">
                            {rows.map(r => (
                                <div key={r.id} className="rounded-lg border border-white/5 bg-[#0a0a0a]" data-testid={`scan-history-row-${r.id}`}>
                                    <button onClick={() => toggleRow(r.id)}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/5 transition">
                                        <ScanLine className="w-4 h-4 text-[#00E5FF] shrink-0" />
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-bold text-white truncate">
                                                {r.customer_name || 'No name on slip'}
                                                <span className="ml-2 text-[11px] font-normal text-[#A0AAB2]">{r.bet_count} bet{r.bet_count === 1 ? '' : 's'}</span>
                                            </p>
                                            <p className="text-[11px] text-[#A0AAB2] truncate">
                                                by {r.actor_name || '—'} · {fmtWhen(r.created_at)}
                                            </p>
                                        </div>
                                        {r.placed_count > 0 ? (
                                            <span className="flex items-center gap-1 text-[10px] font-bold text-[#39FF14] shrink-0" title={`${r.placed_count} placed`}>
                                                <CheckCircle2 className="w-3.5 h-3.5" /> {r.placed_count}
                                            </span>
                                        ) : (
                                            <span className="flex items-center gap-1 text-[10px] text-[#A0AAB2] shrink-0" title="Not placed">
                                                <Clock className="w-3.5 h-3.5" /> —
                                            </span>
                                        )}
                                        {expanded === r.id ? <ChevronUp className="w-4 h-4 text-[#A0AAB2]" /> : <ChevronDown className="w-4 h-4 text-[#A0AAB2]" />}
                                    </button>

                                    {expanded === r.id && (
                                        <div className="px-3 pb-3 pt-1 border-t border-white/5" data-testid={`scan-history-detail-${r.id}`}>
                                            <div className="flex flex-col sm:flex-row gap-3">
                                                {images[r.id] ? (
                                                    <img src={images[r.id]} alt="scanned slip"
                                                         className="w-full sm:w-40 rounded-lg border border-white/10 object-contain max-h-56" />
                                                ) : (
                                                    <div className="w-full sm:w-40 h-40 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-[11px] text-[#A0AAB2]">
                                                        Loading photo…
                                                    </div>
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    {r.customer_phone && <p className="text-[11px] text-[#A0AAB2] mb-1">📞 {r.customer_phone}</p>}
                                                    <div className="flex flex-wrap gap-1">
                                                        {(r.bets_preview || []).map((b, i) => (
                                                            <span key={i} className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-white/10 text-[#FFD700]">
                                                                {betLabel(b)} · {b.state} {(b.drawing_time || '').slice(0, 3)} ${b.amount}
                                                            </span>
                                                        ))}
                                                    </div>
                                                    {r.notes && <p className="text-[10px] text-[#A0AAB2] italic mt-2">AI notes: {r.notes}</p>}
                                                    <p className="text-[10px] mt-2">
                                                        {r.placed_count > 0
                                                            ? <span className="text-[#39FF14] font-bold">✓ {r.placed_count} bet{r.placed_count === 1 ? '' : 's'} placed from this scan</span>
                                                            : <span className="text-[#A0AAB2]">Not submitted (scan only)</span>}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default LotteryScanHistory;
